// src/pages/Dashboard/Dashboard.jsx
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    Stack,
    List,
    ListItem,
    ListItemText,
    Chip,
    Button,
    Skeleton,
    LinearProgress,
    alpha,
    IconButton,
} from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';
import { keyframes } from '@emotion/react';

import {
    Activity,
    CheckCircle2,
    Clock,
    AlertCircle,
    ChevronLeft,
    ChevronRight,
    Microscope,
    Radio,
    BarChart3,
    FlaskConical,
    RefreshCw,
    ArrowUpRight,
} from 'lucide-react';

import { getExperimentos } from '../../infra/api/ExperimentoApi';
import { useBootstrap } from '../../context/BootstrapContext';
import PageHeader from '../../components/PageHeader';
import SaneamentoDashboard from '../../components/SaneamentoDashboard';
import { getEficienciaHistorico } from '../../infra/api/eficienciaApi';
import { listarAmostras } from '../../infra/api/Amostras';
import { getEstacaoConfig, salvarEstacaoConfig } from '../../infra/api/estacaoConfig';
import { usePermissao } from '../../security/usePermissao';
import { usePerfilPlano } from '../../security/usePerfilPlano';
import { LIMITE_PADRAO, BUFFER_PADRAO } from '../../utils/eficiencia';
import { SnackbarAlert } from '../../components/SnackbarAlert';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

// Scrollbar fina e discreta, ciente do tema. Substitui a barra branca/nativa
// (feia nos dois temas) por um "thumb" arredondado e semitransparente derivado
// da cor de texto do tema — some no fundo e ganha contraste no hover.
// Track transparente + thumb com backgroundClip:content-box dá o visual
// flutuante moderno. Inclui fallback do Firefox (scrollbarWidth/Color).
const getScrollbarSx = (theme) => {
    const isDark = theme.palette.mode === 'dark';
    const thumb = alpha(theme.palette.text.primary, isDark ? 0.22 : 0.18);
    const thumbHover = alpha(theme.palette.text.primary, isDark ? 0.4 : 0.34);
    return {
        scrollbarWidth: 'thin',
        scrollbarColor: `${thumb} transparent`,
        '&::-webkit-scrollbar': { width: 8, height: 8 },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: thumb,
            borderRadius: 8,
            border: '2px solid transparent',
            backgroundClip: 'content-box',
            transition: 'background-color 0.2s ease',
        },
        '&::-webkit-scrollbar-thumb:hover': { backgroundColor: thumbHover },
    };
};

// --- HELPERS DE NORMALIZAÇÃO ---
const normalizeId = (val) => {
    if (!val) return '';
    if (typeof val === 'string') {
        const m = val.match(/ObjectId\("([^"]+)"\)/);
        if (m) return m[1];
        try {
            const parsed = JSON.parse(val);
            if (parsed?.$oid) return String(parsed.$oid);
        } catch {}
        return val;
    }
    if (typeof val === 'object' && val?.$oid) return String(val.$oid);
    return String(val);
};

const findAnalysis = (protocol, rawId) => {
    if (!protocol?.analysis || !rawId) return null;
    const normalizedWanted = normalizeId(rawId) || String(rawId);
    return (
        protocol.analysis.find(
            (a, index) =>
                normalizeId(a?.id || a?._id) === normalizedWanted ||
                String(a?.name || a?.nome || `analysis-${index}`) ===
                    normalizedWanted
        ) || null
    );
};

const getProtocolAndAnalysisNameForExperiment = (experiment, protocolsList) => {
    const normalizedProtoId = normalizeId(experiment?.protocolId);
    const normalizedAnalysisId = normalizeId(experiment?.analysisId);

    const protocol = protocolsList.find(
        (p) => normalizeId(p.id || p._id) === normalizedProtoId
    );

    const analysis = protocol
        ? findAnalysis(protocol, normalizedAnalysisId)
        : null;

    return {
        protocolName:
            protocol?.name || protocol?.nome || 'Protocolo Desconhecido',
        analysisName:
            analysis?.name || analysis?.nome || 'Análise Desconhecida',
        protocolColor: protocol?.color || null,
    };
};

// --- COMPONENTES DA UI ---

const StatCard = ({ label, value, icon: Icon, color, delay }) => {
    const theme = useTheme();
    return (
        <Card
            sx={{
                flex: 1,
                minWidth: 0,
                borderRadius: '14px',
                background: theme.palette.background.paper,
                border: `1px solid ${alpha(color, 0.15)}`,
                position: 'relative',
                overflow: 'hidden',
                animation: `fadeIn 0.4s ease-out ${delay}s backwards`,
                '@keyframes fadeIn': {
                    '0%': { opacity: 0, transform: 'translateY(8px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' },
                },
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha(color, 0.18)}`,
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.3)})`,
                },
            }}
        >
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '9px',
                        background: alpha(color, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1,
                    }}
                >
                    <Icon size={17} color={color} />
                </Box>
                <Typography
                    variant="h5"
                    fontWeight={800}
                    color="text.primary"
                    lineHeight={1}
                >
                    {value}
                </Typography>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ mt: 0.5, display: 'block' }}
                >
                    {label}
                </Typography>
            </CardContent>
        </Card>
    );
};

const RecentActivityItem = ({ experiment, protocolName, protocolColor }) => {
    const theme = useTheme();
    const isCompleted = experiment.status === 'completed';
    const statusColor = isCompleted
        ? theme.palette.success.main
        : theme.palette.info.main;
    const dotColor = protocolColor || statusColor;
    const timeLabel = isCompleted
        ? dayjs.unix(experiment.endDate).fromNow()
        : dayjs.unix(experiment.startDate).fromNow();

    return (
        <ListItem
            disableGutters
            sx={{
                py: 0.75,
                px: 1.5,
                mb: 0.5,
                borderRadius: '8px',
                bgcolor: alpha(theme.palette.background.paper, 0.5),
                border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                transition: 'background 0.15s',
                '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                },
            }}
        >
            <Box
                sx={{
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    bgcolor: dotColor,
                    mr: 1.5,
                    flexShrink: 0,
                    boxShadow: `0 0 6px ${alpha(dotColor, 0.6)}`,
                }}
            />
            <ListItemText
                primary={
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                    >
                        <Typography
                            variant="body2"
                            fontWeight={700}
                            noWrap
                            flex={1}
                        >
                            {protocolName}
                        </Typography>
                        <Chip
                            label={isCompleted ? 'Concluído' : 'Ativo'}
                            size="small"
                            sx={{
                                height: 18,
                                fontSize: '0.6rem',
                                fontWeight: 800,
                                bgcolor: alpha(statusColor, 0.1),
                                color: statusColor,
                                border: 'none',
                                '& .MuiChip-label': { px: 0.75 },
                            }}
                        />
                    </Stack>
                }
                secondary={
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        mt={0.2}
                    >
                        <Clock size={10} color={theme.palette.text.disabled} />
                        <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{ fontSize: '0.62rem' }}
                        >
                            {timeLabel} · {experiment.serialNumber}
                        </Typography>
                    </Stack>
                }
                sx={{ m: 0 }}
            />
        </ListItem>
    );
};

const shimmerBar = keyframes`
  0%   { background-position: 100% 0; }
  100% { background-position: 0% 0; }
`;

const RunningExperimentItem = ({
    experiment,
    protocolName,
    analysisName,
    protocolColor,
}) => {
    const theme = useTheme();
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 60000);
        return () => clearInterval(id);
    }, []);

    const accentColor = protocolColor || theme.palette.info.main;
    const warningColor = theme.palette.warning.main;
    const timeStarted = dayjs.unix(experiment.startDate).format('DD/MM HH:mm');

    const TWENTY_FOUR_H = 24 * 60 * 60 * 1000;
    const startMs = experiment.startDate * 1000;
    const elapsed = Math.max(0, now - startMs);
    const progress = Math.min(100, (elapsed / TWENTY_FOUR_H) * 100);
    const isOverdue = elapsed > TWENTY_FOUR_H;

    const totalMinutes = Math.floor(elapsed / 60000);
    const elapsedHours = Math.floor(totalMinutes / 60);
    const elapsedMinutes = totalMinutes % 60;

    const borderColor = isOverdue ? warningColor : accentColor;
    const barColor = isOverdue ? warningColor : accentColor;

    return (
        <Box
            sx={{
                p: 2,
                mb: 1.5,
                borderRadius: '12px',
                border: `1px solid ${alpha(borderColor, isOverdue ? 0.45 : 0.2)}`,
                bgcolor: alpha(isOverdue ? warningColor : accentColor, isOverdue ? 0.06 : 0.03),
                position: 'relative',
                overflow: 'hidden',
                transition: 'border-color 0.3s, background-color 0.3s',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    bgcolor: borderColor,
                    transition: 'background-color 0.3s',
                },
            }}
        >
            {/* Header: icon + name + SN chip */}
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={1.5}
            >
                <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    flex={1}
                    minWidth={0}
                >
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '8px',
                            bgcolor: alpha(accentColor, 0.12),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <FlaskConical size={18} color={accentColor} />
                    </Box>
                    <Box minWidth={0}>
                        <Typography
                            variant="subtitle2"
                            fontWeight={800}
                            noWrap
                            lineHeight={1.2}
                        >
                            {protocolName}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ display: 'block' }}
                        >
                            {analysisName}
                        </Typography>
                    </Box>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                    {isOverdue && (
                        <AlertCircle size={15} color={warningColor} strokeWidth={2.5} />
                    )}
                    <Chip
                        label={experiment.serialNumber || 'SN —'}
                        size="small"
                        variant="outlined"
                        sx={{
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            borderColor: alpha(isOverdue ? warningColor : accentColor, 0.35),
                            color: isOverdue ? warningColor : accentColor,
                            height: 22,
                            '& .MuiChip-label': { px: 1 },
                        }}
                    />
                </Stack>
            </Stack>

            {/* Row: start time + elapsed + status */}
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={0.6}
            >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Clock size={11} color={theme.palette.text.secondary} />
                    <Typography variant="caption" color="text.secondary">
                        Início: {timeStarted}
                    </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography
                        variant="caption"
                        sx={{
                            fontSize: '0.62rem',
                            fontWeight: 600,
                            color: isOverdue
                                ? warningColor
                                : theme.palette.text.secondary,
                        }}
                    >
                        {elapsedHours}h {String(elapsedMinutes).padStart(2, '0')}m decorridos
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            fontSize: '0.58rem',
                            fontWeight: 800,
                            letterSpacing: 1.4,
                            color: isOverdue ? warningColor : accentColor,
                        }}
                    >
                        ● PROCESSANDO
                    </Typography>
                </Stack>
            </Stack>

            {/* Progress bar */}
            <LinearProgress
                variant="determinate"
                value={progress}
                aria-label={`Progresso de processamento para o equipamento ${experiment.serialNumber}`}
                sx={{
                    height: 5,
                    borderRadius: 3,
                    bgcolor: alpha(barColor, 0.12),
                    '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        backgroundColor: barColor,
                        backgroundImage:
                            'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 30%, rgba(255,255,255,0.38) 50%, rgba(255,255,255,0) 70%, rgba(255,255,255,0) 100%)',
                        backgroundSize: '300% 100%',
                        animation: `${shimmerBar} 2s linear infinite`,
                        transition: 'background-color 0.3s',
                    },
                }}
            />

            {/* Time axis labels */}
            <Stack direction="row" justifyContent="space-between" mt={0.4}>
                <Typography
                    variant="caption"
                    sx={{ fontSize: '0.52rem', color: theme.palette.text.secondary }}
                >
                    0h
                </Typography>
                <Typography
                    variant="caption"
                    sx={{ fontSize: '0.52rem', color: theme.palette.text.secondary }}
                >
                    12h
                </Typography>
                <Typography
                    variant="caption"
                    sx={{ fontSize: '0.52rem', color: theme.palette.text.secondary }}
                >
                    24h
                </Typography>
            </Stack>

            {/* Overdue alert banner */}
            {isOverdue && (
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    mt={1}
                    sx={{
                        px: 1.25,
                        py: 0.6,
                        borderRadius: '8px',
                        bgcolor: alpha(warningColor, theme.palette.mode === 'dark' ? 0.12 : 0.08),
                        border: `1px solid ${alpha(warningColor, 0.3)}`,
                    }}
                >
                    <AlertCircle size={12} color={warningColor} strokeWidth={2.5} />
                    <Typography
                        variant="caption"
                        sx={{
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            color: theme.palette.mode === 'dark'
                                ? warningColor
                                : theme.palette.warning.dark,
                        }}
                    >
                        Passou das 24h — {elapsedHours}h {String(elapsedMinutes).padStart(2, '0')}m em processamento
                    </Typography>
                </Stack>
            )}
        </Box>
    );
};

/**
 * Esqueleto da Home. Espelha o layout real (mesmas colunas de grid, mesmas
 * alturas, mesmo `order` do mobile e mesmo padding lateral) para a tela nao
 * "pular" quando os dados chegam.
 *
 * `painelEstacao` vem do perfil do plano (bootstrap), que ja esta em maos antes do fetch — entao
 * da para reservar o espaco do painel de eficiencia desde o primeiro frame, em
 * vez de ele aparecer do nada no meio da pagina e empurrar todo o resto.
 */
const DashboardSkeleton = ({ painelEstacao = false }) => {
    const bloco = { borderRadius: '14px' };

    return (
        <Box
            sx={{
                pt: 0,
                px: { xs: 2, md: 0 },
                pb: { xs: 2, md: 0 },
                // Mesma regra do container real: com o painel de eficiencia a
                // pagina cresce e rola; sem ele ocupa a altura fixa da viewport.
                height: {
                    xs: 'auto',
                    md: painelEstacao ? 'auto' : 'calc(100vh - 160px)',
                },
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            }}
        >
            {/* Header: titulo + subtitulo e o botao de atalho (largura total no mobile) */}
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={2}
                flexShrink={0}
                sx={{ order: { xs: 1, md: 0 } }}
            >
                <Box sx={{ width: '100%' }}>
                    <Skeleton variant="text" width={240} height={38} />
                    {/* Subtitulo = nome do cliente, uma linha curta */}
                    <Skeleton variant="text" width={180} height={20} />
                </Box>
                <Skeleton
                    variant="rectangular"
                    height={40}
                    sx={{
                        borderRadius: '10px',
                        width: { xs: '100%', sm: 170 },
                        flexShrink: 0,
                    }}
                />
            </Stack>

            {/* Stat cards: 2x2 no mobile, 4 colunas no desktop */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: 'repeat(2, 1fr)',
                        sm: 'repeat(4, 1fr)',
                    },
                    gap: 1.5,
                    flexShrink: 0,
                    order: { xs: 3, md: 0 },
                }}
            >
                {[1, 2, 3, 4].map((i) => (
                    <Skeleton
                        key={i}
                        variant="rectangular"
                        height={96}
                        sx={bloco}
                    />
                ))}
            </Box>

            {/* Painel de eficiencia — so no SANEAMENTO, como no render real */}
            {painelEstacao && (
                <Box flexShrink={0} sx={{ order: { xs: 2, md: 0 } }}>
                    <Paper
                        elevation={2}
                        sx={{
                            borderRadius: 3,
                            p: 2,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        }}
                    >
                        <Box sx={{ mb: 2 }}>
                            <Skeleton variant="text" width={280} height={30} />
                            <Skeleton variant="text" width="85%" height={18} />
                        </Box>

                        {/* 5 KPIs: 2 / 3 / 5 colunas */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: 'repeat(2, 1fr)',
                                    sm: 'repeat(3, 1fr)',
                                    md: 'repeat(5, 1fr)',
                                },
                                gap: 1.5,
                                mb: 2,
                            }}
                        >
                            {[1, 2, 3, 4, 5].map((i) => (
                                <Skeleton
                                    key={i}
                                    variant="rectangular"
                                    height={78}
                                    sx={{ borderRadius: 2 }}
                                />
                            ))}
                        </Box>

                        {/* Calendario (esq.) + tendencia (dir.) */}
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            alignItems="stretch"
                        >
                            <Skeleton
                                variant="rectangular"
                                sx={{
                                    flex: { md: 1.15 },
                                    minWidth: 0,
                                    minHeight: 330,
                                    borderRadius: 2,
                                }}
                            />
                            <Skeleton
                                variant="rectangular"
                                sx={{
                                    flex: { md: 1 },
                                    minWidth: 0,
                                    minHeight: 280,
                                    borderRadius: 2,
                                }}
                            />
                        </Stack>
                    </Paper>
                </Box>
            )}

            {/* Conteudo principal: monitoramento primeiro no mobile, log depois */}
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                useFlexGap
                flexGrow={1}
                minHeight={0}
                sx={{ order: { xs: 4, md: 0 } }}
            >
                <Skeleton
                    variant="rectangular"
                    sx={{
                        ...bloco,
                        flex: { md: 1 },
                        minHeight: { xs: 350, md: 200 },
                        order: { xs: 2, md: 0 },
                    }}
                />
                <Skeleton
                    variant="rectangular"
                    sx={{
                        ...bloco,
                        flex: { md: 1 },
                        minHeight: { xs: 400, md: 200 },
                        order: { xs: 1, md: 0 },
                    }}
                />
            </Stack>
        </Box>
    );
};

// --- COMPONENTE PRINCIPAL ---
export default function Dashboard() {
    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.down('sm'));
    const { bootstrap } = useBootstrap();
    const navigate = useNavigate();
    const { tenant } = useParams();

    const { painelEstacao } = usePerfilPlano();

    const protocolsList = useMemo(
        () => bootstrap?.protocolos || [],
        [bootstrap]
    );

    const [activeExperiments, setActiveExperiments] = useState([]);
    const [experimentStats, setExperimentStats] = useState({
        total: 0,
        active: 0,
        completed: 0,
    });
    const [avgAnalysisTime, setAvgAnalysisTime] = useState(null);
    const [recentExperiments, setRecentExperiments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination for active experiments
    const [activeMonitorPage, setActiveMonitorPage] = useState(0);
    const ITEMS_PER_PAGE = 4;

    const [eficienciaData, setEficienciaData] = useState([]);
    const [eficienciaLoading, setEficienciaLoading] = useState(true);

    // No SANEAMENTO os cards contam ANALISES (afluente+efluente), nao experimentos
    // avulsos — e o fluxo inteiro do usuario gira em torno das analises.
    const [analiseStats, setAnaliseStats] = useState({
        total: 0,
        emProcessamento: 0,
        concluidas: 0,
    });

    // Limite regulatório (FEPAM) da estação — quem edita está em capacidades.js.
    const { pode } = usePermissao();
    const [limiteEstacao, setLimiteEstacao] = useState(LIMITE_PADRAO);
    const [bufferEstacao, setBufferEstacao] = useState(BUFFER_PADRAO);
    const [homeSnack, setHomeSnack] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const fetchData = useCallback(async () => {
        try {
            // Busca diretamente os experimentos na ordem pedida à API (dir: 'desc')
            const experimentsResponse = await getExperimentos(
                { sort: 'startDate', dir: 'desc' },
                true
            ).catch(() => []);

            let experimentsList = [];
            if (Array.isArray(experimentsResponse)) {
                experimentsList = experimentsResponse;
            } else if (
                experimentsResponse?.items &&
                Array.isArray(experimentsResponse.items)
            ) {
                experimentsList = experimentsResponse.items;
            }

            // Filtra e organiza os estados
            const activeOnly = experimentsList.filter(
                (e) => e.status === 'active'
            );
            setActiveExperiments(activeOnly);

            const MIN_VALID_TIMESTAMP = dayjs('2019-01-01').unix();

            const completedWithEndDate = experimentsList.filter(
                (e) =>
                    e.status === 'completed' &&
                    e.endDate &&
                    e.endDate.length > 0 &&
                    Number(e.startDate) >= MIN_VALID_TIMESTAMP &&
                    Number(e.endDate) >= MIN_VALID_TIMESTAMP
            );

            // Calcula a média das análises finalizadas
            if (completedWithEndDate.length > 0) {
                const totalDurationHours = completedWithEndDate.reduce(
                    (acc, curr) => {
                        const start = dayjs.unix(curr.startDate);
                        const end = dayjs.unix(curr.endDate);
                        return acc + end.diff(start, 'hours');
                    },
                    0
                );
                const average =
                    totalDurationHours / completedWithEndDate.length;
                setAvgAnalysisTime(`${average.toFixed(1)}h`);
            } else {
                setAvgAnalysisTime('--');
            }

            // Garante ordenação DESCENDENTE (mais recente primeiro) usando o startDate antes de fatiar
            const sortedForLogs = [...experimentsList].sort(
                (a, b) => Number(b.startDate) - Number(a.startDate)
            );

            // Pega os 8 mais recentes para o log de atividades
            setRecentExperiments(sortedForLogs.slice(0, 8));

            setExperimentStats({
                total: experimentsList.length,
                active: activeOnly.length,
                completed: experimentsList.filter(
                    (e) => e.status === 'completed'
                ).length,
            });
        } catch (e) {
            console.error('Erro ao carregar os dados:', e);
            setError('Erro ao carregar dados dos experimentos.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (bootstrap) fetchData();
    }, [bootstrap, fetchData]);

    useEffect(() => {
        if (!bootstrap) return;

        // Conta analises por status pedindo 1 item e lendo o totalElements da pagina:
        // payload minimo, sem trazer a lista inteira (a listagem hoje vem enriquecida
        // com eficiencia, entao baixar tudo so para contar sairia caro).
        const contarAnalises = async (filtro) => {
            const r = await listarAmostras(filtro, 0, 1);
            return r?.totalElements ?? r?.content?.length ?? 0;
        };

        const carregar = () => {
            // 366 registros → cobre ~1 ano p/ o calendário navegar entre meses.
            getEficienciaHistorico(366)
                .then(setEficienciaData)
                .catch((e) => console.error('Falha ao carregar eficiência:', e))
                .finally(() => setEficienciaLoading(false));

            if (!painelEstacao) return;
            Promise.all([
                contarAnalises({}),
                contarAnalises({ status: 'EM_PROCESSAMENTO' }),
                contarAnalises({ status: 'ENCERRADA' }),
                contarAnalises({ status: 'FINALIZADA' }),
            ])
                .then(([total, emProcessamento, encerradas, finalizadas]) =>
                    setAnaliseStats({
                        total,
                        emProcessamento,
                        concluidas: encerradas + finalizadas,
                    })
                )
                .catch((e) =>
                    console.error('Falha ao contar análises:', e)
                );
        };

        carregar();
        const intervalo = setInterval(carregar, 60_000);
        return () => clearInterval(intervalo);
    }, [bootstrap, painelEstacao]);

    // Carrega o limite regulatório da estação (só no painel de estação).
    useEffect(() => {
        if (!bootstrap || !painelEstacao) return;
        getEstacaoConfig()
            .then((cfg) => {
                if (cfg?.limiteRegulatorio != null) setLimiteEstacao(cfg.limiteRegulatorio);
                if (cfg?.bufferAtencao != null) setBufferEstacao(cfg.bufferAtencao);
            })
            .catch((e) => console.error('Falha ao carregar limite regulatório:', e));
    }, [bootstrap, painelEstacao]);

    const handleSalvarLimite = useCallback(
        async (valor) => {
            try {
                const cfg = await salvarEstacaoConfig({
                    limiteRegulatorio: valor,
                    bufferAtencao: bufferEstacao,
                });
                const novo = cfg?.limiteRegulatorio != null ? cfg.limiteRegulatorio : valor;
                setLimiteEstacao(novo);
                if (cfg?.bufferAtencao != null) setBufferEstacao(cfg.bufferAtencao);
                setHomeSnack({
                    open: true,
                    message: `Limite regulatório atualizado para ${novo}%.`,
                    severity: 'success',
                });
            } catch (e) {
                setHomeSnack({
                    open: true,
                    message: e?.message || 'Falha ao salvar o limite regulatório.',
                    severity: 'error',
                });
                throw e; // mantém o diálogo aberto para o usuário corrigir/retentar
            }
        },
        [bufferEstacao]
    );

    // Paginação para o painel de monitoramento da direita
    const totalMonitorPages = Math.ceil(
        activeExperiments.length / ITEMS_PER_PAGE
    );
    const paginatedActiveExps = activeExperiments.slice(
        activeMonitorPage * ITEMS_PER_PAGE,
        (activeMonitorPage + 1) * ITEMS_PER_PAGE
    );

    const clientName =
        bootstrap?.client?.name || bootstrap?.config?.nome || '';

    if (loading) return <DashboardSkeleton painelEstacao={painelEstacao} />;

    if (error)
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                height="calc(100vh - 160px)"
                gap={2}
            >
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.error.main, 0.08),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <AlertCircle size={36} color={theme.palette.error.main} />
                </Box>
                <Typography color="error" variant="h6" fontWeight={700}>
                    {error}
                </Typography>
                <Button
                    variant="outlined"
                    color="error"
                    onClick={fetchData}
                    startIcon={<RefreshCw size={16} />}
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 700,
                    }}
                >
                    Tentar Novamente
                </Button>
            </Box>
        );

    return (
        <>
        <Box
            component="main"
            sx={{
                pt: 0,
                px: { xs: 2, md: 0 },
                pb: { xs: 2, md: 0 },
                backgroundColor: 'transparent',
                height: { xs: 'auto', md: painelEstacao ? 'auto' : 'calc(100vh - 160px)' },
                display: 'flex',
                flexDirection: 'column',
                overflow: { xs: 'visible', md: painelEstacao ? 'auto' : 'hidden' },
                gap: 2,
            }}
        >
            {/* Header
                No mobile a ordem visual e reordenada por CSS `order` (o container raiz e
                flex column): header -> Eficiencia -> Stat Cards -> resto. O laboratorista
                abre a Home no celular para ver a conformidade da estacao primeiro, sem
                rolar. No desktop tudo volta a ordem natural do DOM. */}
            <Stack
                component="header"
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={2}
                flexShrink={0}
                sx={{ order: { xs: 1, md: 0 } }}
            >
                {/* Subtitulo e so o nome do cliente: "Visao geral das analises
                    laboratoriais" nao informava nada que a propria tela ja nao
                    mostrasse, e no mobile roubava uma linha inteira do header. */}
                <PageHeader
                    title="Bem-vindo de volta"
                    subtitle={clientName || undefined}
                />
                {/* No plano SANEAMENTO o fluxo do usuario gira em torno das ANALISES
                    (afluente/efluente), nao das amostras avulsas — entao o atalho leva direto
                    para la. Nos demais planos continua indo para Amostras. */}
                <Button
                    variant="contained"
                    endIcon={<ArrowUpRight size={16} />}
                    onClick={() =>
                        navigate(
                            painelEstacao
                                ? `/${tenant}/analises`
                                : `/${tenant}/amostras`
                        )
                    }
                    sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 700,
                        px: { xs: 2, sm: 3 },
                        alignSelf: { xs: 'stretch', sm: 'auto' },
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                        '&:hover': {
                            boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                        },
                    }}
                >
                    {painelEstacao
                        ? isSm
                            ? 'Análises'
                            : 'Painel de Análises'
                        : isSm
                          ? 'Amostras'
                          : 'Painel de Amostras'}
                </Button>
            </Stack>

            {/* Stat Cards (operacional) — 2×2 no mobile, 4 colunas no desktop */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: 'repeat(2, 1fr)',
                        sm: 'repeat(4, 1fr)',
                    },
                    gap: 1.5,
                    flexShrink: 0,
                    order: { xs: 3, md: 0 },
                }}
            >
                <StatCard
                    label={
                        painelEstacao ? 'Total de Análises' : 'Total de Amostras'
                    }
                    value={
                        painelEstacao
                            ? analiseStats.total
                            : experimentStats.total
                    }
                    icon={Activity}
                    color={theme.palette.primary.main}
                    delay={0.05}
                />
                <StatCard
                    // Conta AMOSTRAS (ensaios) rodando, nao analises: no SANEAMENTO ha
                    // ~1 analise por dia, entao o que o laboratorista precisa ver aqui
                    // e quantas amostras estao em andamento agora.
                    label={
                        painelEstacao
                            ? 'Amostras em Processamento'
                            : 'Em Processamento'
                    }
                    value={experimentStats.active}
                    icon={Radio}
                    color={theme.palette.info.main}
                    delay={0.1}
                />
                <StatCard
                    label={
                        painelEstacao
                            ? 'Análises Concluídas'
                            : 'Amostras Concluídas'
                    }
                    value={
                        painelEstacao
                            ? analiseStats.concluidas
                            : experimentStats.completed
                    }
                    icon={CheckCircle2}
                    color={theme.palette.success.main}
                    delay={0.15}
                />
                <StatCard
                    label="Tempo Médio"
                    value={avgAnalysisTime || '--'}
                    icon={Clock}
                    color={theme.palette.secondary.main}
                    delay={0.2}
                />
            </Box>

            {/* Dashboard de Eficiência (KPIs + calendário + tendência) — só plano SANEAMENTO */}
            {painelEstacao && (
                <Box flexShrink={0} sx={{ order: { xs: 2, md: 0 } }}>
                    <SaneamentoDashboard
                        data={eficienciaData}
                        loading={eficienciaLoading}
                        limite={limiteEstacao}
                        buffer={bufferEstacao}
                        podeEditar={pode('estacao.limiteRegulatorio')}
                        onSalvarLimite={handleSalvarLimite}
                    />
                </Box>
            )}

            <SnackbarAlert
                open={homeSnack.open}
                onClose={() => setHomeSnack((prev) => ({ ...prev, open: false }))}
                message={homeSnack.message}
                severity={homeSnack.severity}
            />

            {/* Main Content */}
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                useFlexGap
                flexGrow={1}
                minHeight={0}
                sx={{ order: { xs: 4, md: 0 } }}
            >
                {/* Left — Activity Log (último no mobile/tablet) */}
                <Paper
                    variant="outlined"
                    component="section"
                    aria-labelledby="activity-log-title"
                    sx={{
                        flex: { md: 1 },
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: { xs: 350, md: 0 },
                        borderRadius: '14px',
                        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        background: alpha(theme.palette.background.paper, 0.6),
                        backdropFilter: 'blur(12px)',
                        overflow: 'hidden',
                        order: { xs: 2, md: 0 },
                    }}
                >
                    <Box
                        sx={{
                            px: 2,
                            py: 1.5,
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <BarChart3
                                size={16}
                                color={theme.palette.text.primary}
                            />
                            <Typography
                                id="activity-log-title"
                                variant="subtitle2"
                                fontWeight={800}
                            >
                                Log de Atividades
                            </Typography>
                        </Stack>
                        <Chip
                            label={`${recentExperiments.length} recentes`}
                            size="small"
                            sx={{
                                height: 20,
                                fontSize: '0.6rem',
                                fontWeight: 700,
                                bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.08
                                ),
                                color: theme.palette.primary.main,
                                '& .MuiChip-label': { px: 0.75 },
                            }}
                        />
                    </Box>
                    <List
                        sx={{
                            p: 1,
                            flexGrow: 1,
                            overflowY: 'auto',
                            ...getScrollbarSx(theme),
                        }}
                    >
                        {recentExperiments.map((exp) => {
                            const { protocolName, protocolColor } =
                                getProtocolAndAnalysisNameForExperiment(
                                    exp,
                                    protocolsList
                                );
                            return (
                                <RecentActivityItem
                                    key={exp.id || exp._id}
                                    experiment={exp}
                                    protocolName={protocolName}
                                    protocolColor={protocolColor}
                                />
                            );
                        })}
                        {recentExperiments.length === 0 && (
                            <Box
                                sx={{
                                    p: 4,
                                    textAlign: 'center',
                                    opacity: 0.45,
                                }}
                            >
                                <Activity
                                    size={36}
                                    color={theme.palette.text.secondary}
                                />
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    mt={1}
                                >
                                    Nenhuma atividade recente encontrada.
                                </Typography>
                            </Box>
                        )}
                    </List>
                </Paper>

                {/* Right — Monitoring (primeiro no mobile/tablet) */}
                <Box
                    component="aside"
                    aria-label="Painel de Monitoramento de Experimentos Ativos"
                    sx={{
                        flex: { md: 1 },
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: { xs: 400, md: 0 },
                        order: { xs: 1, md: 0 },
                    }}
                >
                    <Paper
                        variant="outlined"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            minHeight: 0,
                            borderRadius: '14px',
                            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                            background: alpha(
                                theme.palette.background.paper,
                                0.6
                            ),
                            backdropFilter: 'blur(12px)',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Monitoring Header */}
                        <Box
                            sx={{
                                px: 2,
                                py: 1.5,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                            >
                                <Microscope
                                    size={18}
                                    color="#fff"
                                    aria-hidden="true"
                                />
                                <Typography
                                    variant="subtitle2"
                                    fontWeight={800}
                                    color="white"
                                >
                                    Monitoramento
                                </Typography>
                                {experimentStats.active > 0 && (
                                    <Chip
                                        label={`${experimentStats.active} online`}
                                        size="small"
                                        sx={{
                                            height: 18,
                                            fontSize: '0.6rem',
                                            fontWeight: 800,
                                            bgcolor: 'rgba(255,255,255,0.2)',
                                            color: '#fff',
                                            '& .MuiChip-label': { px: 0.75 },
                                        }}
                                    />
                                )}
                            </Stack>
                            {totalMonitorPages > 1 && (
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    aria-label="Paginação do monitoramento"
                                >
                                    <IconButton
                                        size="small"
                                        aria-label="Página anterior"
                                        onClick={() =>
                                            setActiveMonitorPage((p) =>
                                                Math.max(0, p - 1)
                                            )
                                        }
                                        disabled={activeMonitorPage === 0}
                                        sx={{
                                            color: '#fff',
                                            '&.Mui-disabled': {
                                                color: 'rgba(255,255,255,0.3)',
                                            },
                                        }}
                                    >
                                        <ChevronLeft size={18} />
                                    </IconButton>
                                    <Typography
                                        variant="caption"
                                        aria-live="polite"
                                        sx={{
                                            color: '#fff',
                                            fontWeight: 700,
                                            mx: 0.5,
                                        }}
                                    >
                                        {activeMonitorPage + 1}/
                                        {totalMonitorPages}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        aria-label="Próxima página"
                                        onClick={() =>
                                            setActiveMonitorPage((p) =>
                                                Math.min(
                                                    totalMonitorPages - 1,
                                                    p + 1
                                                )
                                            )
                                        }
                                        disabled={
                                            activeMonitorPage >=
                                            totalMonitorPages - 1
                                        }
                                        sx={{
                                            color: '#fff',
                                            '&.Mui-disabled': {
                                                color: 'rgba(255,255,255,0.3)',
                                            },
                                        }}
                                    >
                                        <ChevronRight size={18} />
                                    </IconButton>
                                </Stack>
                            )}
                        </Box>

                        <Box
                            sx={{
                                flexGrow: 1,
                                p: 2,
                                overflowY: 'auto',
                                ...getScrollbarSx(theme),
                            }}
                        >
                            {paginatedActiveExps.length > 0 ? (
                                paginatedActiveExps.map((exp) => {
                                    const {
                                        protocolName,
                                        analysisName,
                                        protocolColor,
                                    } = getProtocolAndAnalysisNameForExperiment(
                                        exp,
                                        protocolsList
                                    );
                                    return (
                                        <RunningExperimentItem
                                            key={exp.id || exp._id}
                                            experiment={exp}
                                            protocolName={protocolName}
                                            analysisName={analysisName}
                                            protocolColor={protocolColor}
                                        />
                                    );
                                })
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        gap: 1.5,
                                        opacity: 0.45,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            borderRadius: '50%',
                                            bgcolor: alpha(
                                                theme.palette.primary.main,
                                                0.08
                                            ),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <CheckCircle2
                                            size={32}
                                            color={theme.palette.primary.main}
                                        />
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        fontWeight={700}
                                    >
                                        Sem amostras em processamento.
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Todos os equipamentos estão livres.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Box>
            </Stack>
        </Box>
        </>
    );
}
